import React, { useState, type CSSProperties } from 'react';
import HmdCastView from './HmdCastView';
import type { Notification } from '../types/database';

// NoVrVerifyModal: the no-VR / stream-teardown banner opens this instead of
// acting blind. The headset live view sits on top so the operator sees what
// the visitor sees, then picks the cheapest lever that fits: restart the
// headset's streaming client (the "kick"), the agent's auto-repair, or "vals
// alarm" when the picture is fine. Every choice carries what the operator saw,
// and the tile writes that verdict onto the incident row, which is how
// detector precision per failure class becomes measurable over time
// (RealityKnowledge systems/headset-stream-failure-classes). The modal itself
// writes nothing and never throws; the parent owns toasts and the agent calls.

/** What the operator saw in the live view (or by looking into the headset). */
export type NoVrSaw = 'streaming' | 'passthrough_client' | 'black' | 'no_view';
/** Which lever the operator chose.
 *  kick     — restart the streaming client on the HEADSET only (cheapest).
 *  kick_pc  — restart Business Streaming on the PC *and* kick the headset. The
 *             step up when the headset-only kick did nothing, and the only one
 *             that works when the PC side is what died: the headset-only call
 *             needs a serial that is read from Business Streaming itself. */
export type NoVrVerdictKind = 'kick' | 'kick_pc' | 'repair' | 'false_alarm';

export interface NoVrRepairStatusView {
  running: boolean;
  phase: string;
  recovered: boolean;
  needsReboot: boolean;
  message: string;
}

export interface NoVrVerifyModalProps {
  pcName: string;
  notification: Notification;
  /** Cast source; null when the shell is offline or the tile has no agent. */
  liveView: { host: string; serial: string; stopCast: () => Promise<unknown> } | null;
  /** Shell new enough to cast (HeadsetSheet's HMD_CAST_MIN_SHELL check). */
  castable: boolean;
  /** Shown instead of the cast when it cannot run (e.g. simulated tile). */
  castHint?: string;
  busy: boolean;
  canRestartStreaming: boolean;
  repairStatus: NoVrRepairStatusView | null;
  onAction: (kind: NoVrVerdictKind, saw: NoVrSaw) => void | Promise<void>;
  onClose: () => void;
}

const SAW_OPTIONS: { key: NoVrSaw; label: string; hint: string }[] = [
  { key: 'streaming', label: 'Ervaring loopt', hint: 'De bezoeker ziet de ervaring: dit is een vals alarm.' },
  { key: 'passthrough_client', label: 'Camerabeeld / BS-venster', hint: 'Passthrough met het Business Streaming-venster: de headset vraagt de stream niet aan.' },
  { key: 'black', label: 'Zwart / bevroren', hint: 'Geen beeld of een stilstaand beeld.' },
  { key: 'no_view', label: 'Kon niet kijken', hint: 'Geen live beeld beschikbaar; verdict zonder kijken.' },
];

export default function NoVrVerifyModal({
  pcName,
  notification,
  liveView,
  castable,
  castHint,
  busy,
  canRestartStreaming,
  repairStatus,
  onAction,
  onClose,
}: NoVrVerifyModalProps) {
  const [saw, setSaw] = useState<NoVrSaw | null>(null);
  const [acting, setActing] = useState<NoVrVerdictKind | null>(null);
  const showCast = !!liveView && castable;

  const act = async (kind: NoVrVerdictKind) => {
    const chosen = saw ?? (showCast ? 'no_view' : 'no_view');
    setActing(kind);
    try {
      await onAction(kind, chosen);
    } finally {
      setActing(null);
    }
  };

  const btn = (color: string, disabled: boolean): CSSProperties => ({
    padding: '10px 14px',
    borderRadius: 10,
    border: 'none',
    background: disabled ? '#c7ced6' : color,
    color: '#fff',
    fontWeight: 600,
    fontSize: 14,
    cursor: disabled ? 'not-allowed' : 'pointer',
    flex: '1 1 160px',
  });

  const rs = repairStatus;
  const repairBusy = !!rs?.running;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(16,32,46,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={() => {
        if (!acting) onClose();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 14,
          padding: 20,
          width: 'min(760px, calc(100vw - 24px))',
          maxHeight: 'calc(100vh - 24px)',
          overflowY: 'auto',
          boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
          color: '#16202e',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 style={{ margin: 0 }}>Geen beeld in de headset · {pcName}</h3>
          <div style={{ fontSize: 13, color: '#c0392b', marginTop: 4 }}>{notification.text}</div>
        </div>

        {/* Live view first: the one signal that cannot be wrong. */}
        <div style={{ minHeight: 160, background: '#0f1720', borderRadius: 10, overflow: 'hidden', position: 'relative' }}>
          {showCast && liveView ? (
            <HmdCastView host={liveView.host} serial={liveView.serial} profile="normal" stopCast={liveView.stopCast} onClose={onClose} />
          ) : (
            <div style={{ color: '#c7ced6', fontSize: 13, padding: 16, textAlign: 'center' }}>
              {castHint ||
                (liveView
                  ? 'Deze headset-shell kan nog niet meekijken. Kijk zelf in de headset en kies hieronder wat je zag.'
                  : 'Geen verbinding met de headset-shell, dus geen live beeld. Kijk zelf in de headset en kies hieronder wat je zag.')}
            </div>
          )}
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#54626f', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            Wat zie je?
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {SAW_OPTIONS.map((o) => {
              const sel = saw === o.key;
              return (
                <button
                  key={o.key}
                  title={o.hint}
                  onClick={() => setSaw(o.key)}
                  disabled={!!acting}
                  style={{
                    padding: '7px 14px',
                    borderRadius: 100,
                    fontSize: 13,
                    cursor: 'pointer',
                    border: sel ? '1px solid #2B6AC0' : '1px solid #d5dce5',
                    background: sel ? '#2B6AC0' : '#fff',
                    color: sel ? '#fff' : '#16202e',
                    fontWeight: sel ? 600 : 400,
                  }}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>

        {rs && (
          <div style={{ fontSize: 13, color: rs.needsReboot ? '#c0392b' : rs.recovered ? '#0e8a5a' : '#54626f' }}>
            {rs.running
              ? `⏳ Auto-herstel loopt… (${rs.phase})`
              : rs.recovered
                ? '✓ Streaming hersteld'
                : rs.needsReboot
                  ? `⟳ ${rs.message || 'Auto-herstel mislukt, herstart van de PC aanbevolen'}`
                  : rs.message}
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <button
            style={btn('#0e8a8a', busy || !!acting || !canRestartStreaming || repairBusy)}
            disabled={busy || !!acting || !canRestartStreaming || repairBusy}
            title="Herstart de streaming-client op de headset (2 s). Goedkoopste stap: de headset kiest opnieuw in bij de PC."
            onClick={() => act('kick')}
          >
            {acting === 'kick' ? '…' : 'Herstart streaming (headset)'}
          </button>
          <button
            style={btn('#0e6f8a', busy || !!acting || repairBusy)}
            disabled={busy || !!acting || repairBusy}
            title="Herstart Business Streaming op de PC én op de headset (tot ~1 min). Gebruik dit als de headset-herstart niets deed, of als de PC-kant vastzit."
            onClick={() => act('kick_pc')}
          >
            {acting === 'kick_pc' ? '…' : 'Herstart streaming (PC + headset)'}
          </button>
          <button
            style={btn('#2B6AC0', busy || !!acting || repairBusy)}
            disabled={busy || !!acting || repairBusy}
            title="Stopt de ervaring en laat de agent de PC-kant herstellen (display, Business Streaming, kick, boottest)."
            onClick={() => act('repair')}
          >
            {acting === 'repair' ? '…' : 'Auto-herstel'}
          </button>
          <button
            style={btn('#0e8a5a', !!acting || repairBusy)}
            disabled={!!acting || repairBusy}
            title="Het beeld is goed: sluit de melding en markeer het incident als vals alarm."
            onClick={() => act('false_alarm')}
          >
            {acting === 'false_alarm' ? '…' : 'Vals alarm, beeld is goed'}
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: '#8a97a6' }}>
          <span>Je keuze en wat je zag worden op het incident vastgelegd.</span>
          <button
            onClick={onClose}
            disabled={!!acting}
            style={{ background: 'none', border: 'none', color: '#2B6AC0', cursor: 'pointer', fontSize: 14 }}
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}
